import { DrawCabinet } from "$src/CGS/domain/dto/article/Cabinet/DrawCabinet";
import { DrawCabinetFrontElement } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawCabinetFrontElement";
import { DrawCabinetFrontElementGroup } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawCabinetFrontElementGroup";
import { DrawFrontDoor } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawFrontDoor";
import { DrawFrontDrawer } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawFrontDrawer";
import { DrawFrontPullOut } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawFrontPullOut";
import { DrawConstant } from "$src/CGS/domain/dto/constant/DrawConstant";
import { EOpening } from "$src/CGS/domain/dto/core/EOpening";
import { EOpeningType } from "$src/CGS/domain/dto/core/EOpeningType";
import { EJoinType } from "$src/CGS/domain/dto/core/FrontElementParameter/EJoinType";
import LengthValue from "$src/CGS/domain/dto/core/Length/LengthValue";
import { Vector3 } from "three";
import { CabinetGeneratorErrorRegistry } from "../../Errors/CabinetGeneratorErrorRegistry";
import { FrontHandleMetadata,  EHandlePosition, VerticalSide, HorizontalSide, EOrientation } from "../FrontHandleMetadata";
import EItemTypeExt from "$src/CGS/domain/dto/core/EItemTypeExt";
import { EOrientation as EHandleOrientation } from "$src/CGS/domain/dto/core/EOrientation";

export class FrontHandleMetadataBuilder {
    private readonly _constants: DrawConstant;
    private readonly _cabinet: DrawCabinet;
    private readonly _errorRegistry: CabinetGeneratorErrorRegistry;

    private static readonly TOO_HIGH = 1.6;
    private static readonly TOO_LOW = 0.4;

    constructor(cabinet: DrawCabinet, errorRegistry: CabinetGeneratorErrorRegistry) {
        this._cabinet = cabinet;
        this._constants = cabinet.Constants;
        this._errorRegistry = errorRegistry;
    }

    public buildFrontHandles(width: LengthValue, height: LengthValue, anchor: Vector3, context: DrawCabinetFrontElement, groupContext: DrawCabinetFrontElementGroup): FrontHandleMetadata[] | null {
        if (!this._cabinet.Range?.HasHandles) {
            return null;
        }

        const frontHandleMetadatas: FrontHandleMetadata[] = [];

        let data = this.buildFrontHandlesOnPositionCenter(width, height, anchor, context, groupContext);
        if (data != null) {
            frontHandleMetadatas.push(data);
        }

        data = this.buildFrontHandlesOnPositionVertical(width, height, anchor, context, groupContext);
        if (data != null) {
            frontHandleMetadatas.push(data);
        }

        data = this.BuildFrontHandlesOnPositionHorizontal(width, height, anchor, context, groupContext);
        if (data != null) {
            frontHandleMetadatas.push(data);
        }

        data = this.BuildFrontHandlesOnPositionEdgeVertical(width, height, anchor, context, groupContext);
        if (data != null) {
            frontHandleMetadatas.push(data);
        }

        return frontHandleMetadatas;
    }

    private buildFrontHandlesOnPositionCenter(width: LengthValue, height: LengthValue, anchor: Vector3, context: DrawCabinetFrontElement, groupContext: DrawCabinetFrontElementGroup): FrontHandleMetadata | null {
        if (context instanceof DrawFrontDoor) {
            const door = context as DrawFrontDoor;
            if (door.IsPushPull) {
                return null;
            }

            let verticalAlign: VerticalSide;
            const horizontalAlign = HorizontalSide.Center;
            const orientation = EOrientation.Normal;

            if ([EOpeningType.L, EOpeningType.VL, EOpeningType.F].includes(door.openingType)) {
                verticalAlign = VerticalSide.Bottom;
            } else if (door.openingType === EOpeningType.B) {
                verticalAlign = VerticalSide.Top;
            } else if (groupContext && groupContext.JoinType === EJoinType.F && groupContext.Orientation === EHandleOrientation.Vertical) {
                verticalAlign = VerticalSide.Bottom;
            } else if (  EItemTypeExt.isWallCabinet(this._cabinet.Type)) {
                verticalAlign = VerticalSide.Bottom;
            } else if (anchor.y > this._constants.CountertopHeight.meter) {
                verticalAlign = VerticalSide.Bottom;
            } else if (anchor.y + height.meter < this._constants.CountertopHeight.meter) {
                verticalAlign = VerticalSide.Top;
            } else {
                if (anchor.y + height.meter >= FrontHandleMetadataBuilder.TOO_HIGH) {
                    if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                        verticalAlign = VerticalSide.Center;
                    } else {
                        verticalAlign = VerticalSide.Bottom;
                    }
                } else if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                    verticalAlign = VerticalSide.Top;
                } else {
                    verticalAlign = VerticalSide.Bottom;
                }
            }

            return {
                VerticalAlign: verticalAlign,
                HorizontalAlign: horizontalAlign,
                Orientation: orientation,
                Position: EHandlePosition.DefaultCenter
            };
        } else if (context instanceof DrawFrontPullOut) {
            const pullout = context as DrawFrontPullOut;
            if (pullout.IsPushPull) {
                return null;
            }

            let verticalAlign: VerticalSide;

            if (anchor.y > this._constants.CountertopHeight.meter) {
                verticalAlign = VerticalSide.Bottom;
            } else if (anchor.y + height.meter < this._constants.CountertopHeight.meter) {
                verticalAlign = VerticalSide.Top;
            } else {
                if (anchor.y + height.meter >= FrontHandleMetadataBuilder.TOO_HIGH) {
                    if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                        verticalAlign = VerticalSide.Top;
                    } else {
                        verticalAlign = VerticalSide.Bottom;
                    }
                } else if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                    verticalAlign = VerticalSide.Top;
                } else {
                    verticalAlign = VerticalSide.Bottom;
                }
            }

            return {
                VerticalAlign: verticalAlign,
                HorizontalAlign: HorizontalSide.Center,
                Position: EHandlePosition.DefaultCenter,
                Orientation: EOrientation.Normal
            };
        } else if (context instanceof DrawFrontDrawer) {
            const drawer = context as DrawFrontDrawer;
            if (drawer.IsPushPull) {
                return null;
            }

            return {
                VerticalAlign: VerticalSide.Top,
                HorizontalAlign: HorizontalSide.Center,
                Position: EHandlePosition.DefaultCenter,
                Orientation: EOrientation.Normal
            };
        }

        return null;
    }

    private buildFrontHandlesOnPositionVertical(width: LengthValue, height: LengthValue, anchor: Vector3, context: DrawCabinetFrontElement, groupContext: DrawCabinetFrontElementGroup) 
        : FrontHandleMetadata | null {

        if (context instanceof DrawFrontDoor) {
            const door = context as DrawFrontDoor;
            if (door.IsPushPull) {
                return null;
            }

            let verticalAlign: VerticalSide;
            let horizontalAlign = HorizontalSide.Right;
            const openingOrientation = door.OpeningOrientation ?? EOpening.R;

            if (openingOrientation === EOpening.L) {
                horizontalAlign = HorizontalSide.Left;
            }

            let orientation = EOrientation.Normal;
            if ([EOpeningType.L, EOpeningType.VL, EOpeningType.F].includes(door.openingType)) {
                verticalAlign = VerticalSide.Bottom;
                horizontalAlign = HorizontalSide.Center;
            } else if (door.openingType === EOpeningType.B) {
                verticalAlign = VerticalSide.Top;
                horizontalAlign = HorizontalSide.Center;
            } else if (groupContext && groupContext.JoinType === EJoinType.F && groupContext.Orientation === EHandleOrientation.Vertical) {
                verticalAlign = VerticalSide.Bottom;
                horizontalAlign = HorizontalSide.Center;
            } else if ( EItemTypeExt.isWallCabinet(this._cabinet.Type) ) {
                verticalAlign = VerticalSide.Bottom;
            } else if (anchor.y > this._constants.CountertopHeight.meter) {
                verticalAlign = VerticalSide.Bottom;
            } else if (anchor.y + height.meter < this._constants.CountertopHeight.meter) {
                verticalAlign = VerticalSide.Top;
            } else {
                verticalAlign = VerticalSide.Center;

                if (anchor.y + height.meter >= FrontHandleMetadataBuilder.TOO_HIGH) {
                    if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                        verticalAlign = VerticalSide.Center;
                    } else {
                        verticalAlign = VerticalSide.Bottom;
                    }
                } else if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                    verticalAlign = VerticalSide.Top;
                } else {
                    verticalAlign = VerticalSide.Bottom;
                }
            }

            if (horizontalAlign === HorizontalSide.Left) {
                orientation = EOrientation.Right;
            } else if (horizontalAlign === HorizontalSide.Right) {
                orientation = EOrientation.Left;
            }

            return {
                VerticalAlign: verticalAlign,
                HorizontalAlign: horizontalAlign,
                Orientation: orientation,
                Position: EHandlePosition.DefaultVertical
            };
        } else if (context instanceof DrawFrontPullOut) {
            if (context.IsPushPull)
                return null;

            let verticalAlign: any;

            if (anchor.y > this._constants.CountertopHeight.meter) {
                verticalAlign = 'Bottom';
            } else if (anchor.y + height.meter < this._constants.CountertopHeight.meter) {
                verticalAlign = 'Top';
            } else {
                if (anchor.y + height.meter >= FrontHandleMetadataBuilder.TOO_HIGH) {
                    if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                        verticalAlign = 'Top';
                    } else {
                        verticalAlign = 'Bottom';
                    }
                } else if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                    verticalAlign = 'Top';
                } else {
                    verticalAlign = 'Bottom';
                }
            }

            return {
                VerticalAlign: verticalAlign,
                HorizontalAlign: HorizontalSide.Center,
                Position: EHandlePosition.DefaultVertical,
                Orientation: EOrientation.Normal
            };
        } else if (context instanceof DrawFrontDrawer) {
            if (context.IsPushPull)
                return null;

            return {
                VerticalAlign:  VerticalSide.Top,
                HorizontalAlign: HorizontalSide.Center,
                Position: EHandlePosition.DefaultVertical,
                Orientation: EOrientation.Normal
            };
        }

        return null;
    }

    BuildFrontHandlesOnPositionHorizontal(width: LengthValue, height: LengthValue, anchor: Vector3, context: DrawCabinetFrontElement, groupContext: DrawCabinetFrontElementGroup): FrontHandleMetadata | null {
        if (context instanceof DrawFrontDoor) {
            if (context.IsPushPull)
                return null;

            let verticalAlign: any;
            let horizontalAlign: any;

            horizontalAlign = 'Right';

            const openingOrientation = context.OpeningOrientation ?? EOpening.L;

            if (openingOrientation == EOpening.L)
                horizontalAlign = 'Left';

            let orientation: any = 'Normal';

            if (context.openingType == EOpeningType.L || context.openingType == EOpeningType.VL || context.openingType == EOpeningType.F) {
                verticalAlign = 'Bottom';
                horizontalAlign = 'Center';
            } else if (context.openingType == EOpeningType.B) {
                verticalAlign = 'Top';
                horizontalAlign = 'Center';
            } else if (groupContext != null && groupContext.JoinType == EJoinType.F && groupContext.Orientation == EHandleOrientation.Vertical) {
                verticalAlign = 'Bottom';
                horizontalAlign = 'Center';
            } else if ( EItemTypeExt.isWallCabinet(this._cabinet.Type)) {
                verticalAlign = 'Bottom';
            } else if (anchor.y > this._constants.CountertopHeight.meter) {
                verticalAlign = 'Bottom';
            } else if (anchor.y + height.meter < this._constants.CountertopHeight.meter) {
                verticalAlign = 'Top';
            } else {
                verticalAlign = 'Center';

                if (anchor.y + height.meter >= FrontHandleMetadataBuilder.TOO_HIGH) {
                    if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                        verticalAlign = 'Center';
                    } else {
                        verticalAlign = 'Bottom';
                    }
                } else if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                    verticalAlign = 'Top';
                } else {
                    verticalAlign = 'Bottom';
                }
            }

            return {
                VerticalAlign: verticalAlign,
                HorizontalAlign: horizontalAlign,
                Orientation: orientation,
                Position: EHandlePosition.DefaultHorizontal
            };
        } else if (context instanceof DrawFrontPullOut) {
            if (context.IsPushPull)
                return null;

            let verticalAlign: any;

            if (anchor.y > this._constants.CountertopHeight.meter) {
                verticalAlign = 'Bottom';
            } else if (anchor.y + height.meter < this._constants.CountertopHeight.meter) {
                verticalAlign = 'Top';
            } else {
                if (anchor.y + height.meter >= FrontHandleMetadataBuilder.TOO_HIGH) {
                    if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                        verticalAlign = 'Top';
                    } else {
                        verticalAlign = 'Bottom';
                    }
                } else if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                    verticalAlign = 'Top';
                } else {
                    verticalAlign = 'Bottom';
                }
            }

            return {
                VerticalAlign: verticalAlign,
                HorizontalAlign: HorizontalSide.Center,
                Position: EHandlePosition.DefaultHorizontal,
                Orientation: EOrientation.Normal
            };
        } else if (context instanceof DrawFrontDrawer) {
            if (context.IsPushPull)
                return null;

            return {
                VerticalAlign: VerticalSide.Top,
                HorizontalAlign: HorizontalSide.Center,
                Position:  EHandlePosition.DefaultHorizontal,
                Orientation: EOrientation.Normal
            };
        }

        return null;
    }

    BuildFrontHandlesOnPositionEdgeVertical(width: LengthValue, height: LengthValue, anchor: Vector3, context: DrawCabinetFrontElement, groupContext: DrawCabinetFrontElementGroup): FrontHandleMetadata | null {
        if (context instanceof DrawFrontDoor) {
            if (context.IsPushPull)
                return null;

            let verticalAlign: VerticalSide;
            let horizontalAlign: HorizontalSide;

            horizontalAlign = HorizontalSide.Right;

            const openingOrientation = context.OpeningOrientation ?? EOpening.L;

            if (openingOrientation == EOpening.L)
                horizontalAlign = HorizontalSide.Left;

            let orientation: EOrientation = EOrientation.Normal;
            if (context.openingType == EOpeningType.L || context.openingType == EOpeningType.VL || context.openingType == EOpeningType.F) {
                verticalAlign = VerticalSide.Bottom;
                horizontalAlign = HorizontalSide.Center;
            } else if (context.openingType == EOpeningType.B) {
                verticalAlign = VerticalSide.Top;
                horizontalAlign = HorizontalSide.Center;
            } else if (groupContext != null && groupContext.JoinType == EJoinType.F && groupContext.Orientation == EHandleOrientation.Vertical) {
                verticalAlign =  VerticalSide.Bottom;
                horizontalAlign = HorizontalSide.Center;
            } else {
                verticalAlign = VerticalSide.Center;
            }

            if (horizontalAlign == HorizontalSide.Left)
                orientation = EOrientation.Right;
            else if (horizontalAlign == HorizontalSide.Right)
                orientation = EOrientation.Left;

            return {
                VerticalAlign: verticalAlign,
                HorizontalAlign: horizontalAlign,
                Orientation: orientation,
                Position:  EHandlePosition.CenterVertical
            };
        } else if (context instanceof DrawFrontPullOut) {
            if (context.IsPushPull)
                return null;

            let verticalAlign: any;

            if (anchor.y > this._constants.CountertopHeight.meter) {
                verticalAlign = VerticalSide.Bottom;
            } else if (anchor.y + height.meter < this._constants.CountertopHeight.meter) {
                verticalAlign = VerticalSide.Top;
            } else {
                if (anchor.y + height.meter >= FrontHandleMetadataBuilder.TOO_HIGH) {
                    if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                        verticalAlign = VerticalSide.Top;
                    } else {
                        verticalAlign = VerticalSide.Bottom;
                    }
                } else if (anchor.y < FrontHandleMetadataBuilder.TOO_LOW) {
                    verticalAlign = VerticalSide.Top;
                } else {
                    verticalAlign = VerticalSide.Bottom;
                }
            }

            return {
                VerticalAlign:  verticalAlign,
                HorizontalAlign: HorizontalSide.Center,
                Position: EHandlePosition.CenterVertical,
                Orientation: EOrientation.Normal
            };
        } else if (context instanceof DrawFrontDrawer) {
            if (context.IsPushPull)
                return null;

            return {
                VerticalAlign: VerticalSide.Top,
                HorizontalAlign: HorizontalSide.Center,
                Position:  EHandlePosition.CenterVertical,
                Orientation: EOrientation.Normal
            };
        }

        return null;
    }
}
